'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowDown02Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
} from '@terra/ui-icons';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Badge } from '../../../../../components/badge';
import { IconButton } from '../../../../../components/icon-button';
import { Text } from '../../../../../components/text';
import { formatStreamingMessageTime } from '../../../../streaming-messages/format-streaming-message-time';
import { WebSocketExecutionShell } from './WebSocketExecutionShell';
import type {
  WebSocketInboundMessage,
  WebSocketPollView,
} from './parse-websocket-execution-view';

type WebSocketPollViewProps = {
  view: WebSocketPollView;
  maxHeight: string;
};
const PAYLOAD_PREVIEW_LENGTH = 80;

function previewPayload(data: string): string {
  return data.length > PAYLOAD_PREVIEW_LENGTH
    ? `${data.slice(0, PAYLOAD_PREVIEW_LENGTH)}…`
    : data;
}

function InboundMessageRow({
  message,
  isExpanded,
  onToggle,
}: {
  message: WebSocketInboundMessage;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations('designSystem.chat.websocketClient');
  const formattedTimestamp = formatStreamingMessageTime(message.timestamp);
  const preview = message.data ? previewPayload(message.data) : null;

  return (
    <Box
      borderWidth="1px"
      borderColor="border.subtle"
      borderRadius="md"
      width="100%"
    >
      <HStack
        p={1.5}
        justify="space-between"
        cursor="pointer"
        onClick={onToggle}
        _hover={{ bg: 'bg.subtle' }}
        width="100%"
      >
        <HStack gap={1.5} flex="1" minW={0}>
          <IconButton size="xs" variant="ghost" aria-label={t('toggleMessage')}>
            {isExpanded ? (
              <ArrowDown01Icon size={12} />
            ) : (
              <ArrowRight01Icon size={12} />
            )}
          </IconButton>
          <ArrowDown02Icon size={10} color="var(--colors-fg-muted)" />
          {preview ? (
            <Text
              fontSize="xs"
              fontFamily="mono"
              color="fg.default"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              flex="1"
            >
              {preview}
            </Text>
          ) : (
            <Text fontSize="xs" color="fg.muted">
              {t('noData')}
            </Text>
          )}
        </HStack>
        {formattedTimestamp ? (
          <Text
            fontSize="xs"
            color="fg.subtle"
            fontFamily="mono"
            flexShrink={0}
          >
            {formattedTimestamp}
          </Text>
        ) : null}
      </HStack>
      {isExpanded ? (
        <Box
          borderTopWidth="1px"
          borderColor="border.subtle"
          p={2}
          bg="bg.canvas"
        >
          {message.data ? (
            <Box p={2} bg="bg.muted" borderRadius="sm" width="100%">
              <Text
                fontSize="xs"
                fontFamily="mono"
                color="fg.subtle"
                whiteSpace="pre-wrap"
                wordBreak="break-all"
              >
                {message.data}
              </Text>
            </Box>
          ) : (
            <Text fontSize="xs" color="fg.muted">
              {t('noData')}
            </Text>
          )}
        </Box>
      ) : null}
    </Box>
  );
}

export function WebSocketPollViewPanel({
  view,
  maxHeight,
}: WebSocketPollViewProps) {
  const t = useTranslations('designSystem.chat.websocketClient');
  const tChat = useTranslations('designSystem.chat');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const hasMessages = view.messages.length > 0;

  return (
    <WebSocketExecutionShell
      mode="poll"
      testId="websocket-execution-poll"
      error={view.error}
      status={
        <Badge size="sm" variant="fill">
          {view.error
            ? tChat('failed')
            : t('messagesReceived', { count: view.count })}
        </Badge>
      }
    >
      <HStack gap={1.5} justifyContent="space-between">
        <Text fontSize="xs" color="fg.muted">
          {hasMessages
            ? t('messagesReceived', { count: view.count })
            : t('noMessagesReceived')}
        </Text>
        {view.isActive === undefined ? null : (
          <Badge size="sm" variant="fill">
            {view.isActive ? t('active') : t('closed')}
          </Badge>
        )}
      </HStack>
      {view.sessionId ? (
        <HStack gap={1}>
          <Text fontSize="xs" color="fg.muted" flexShrink={0}>
            {t('sessionId')}
          </Text>
          <Text
            fontSize="xs"
            fontFamily="mono"
            color="fg.subtle"
            wordBreak="break-all"
          >
            {view.sessionId}
          </Text>
        </HStack>
      ) : null}
      {hasMessages ? (
        <VStack
          gap={1.5}
          width="100%"
          maxHeight={maxHeight}
          overflowY="auto"
          alignItems="stretch"
        >
          {view.messages.map((message) => (
            <InboundMessageRow
              key={message.id}
              message={message}
              isExpanded={expandedId === message.id}
              onToggle={() =>
                setExpandedId(expandedId === message.id ? null : message.id)
              }
            />
          ))}
        </VStack>
      ) : (
        <Box bg="bg.subtle" p={3} borderRadius="sm" width="100%">
          <Text fontSize="xs" color="fg.muted" textAlign="center">
            {t('noMessagesReceived')}
          </Text>
        </Box>
      )}
    </WebSocketExecutionShell>
  );
}
